import { CalendarClock, ExternalLink, Pencil, User } from 'lucide-react';
import { Badge } from '@/shared/components/ui/Badge';
import { STATUS_LABEL, STATUS_TONE, formatWhen } from '../constants';
import type { Meeting } from '../types';

interface MeetingCardProps {
  meeting: Meeting;
  canEdit: boolean;
  onOpen: (meeting: Meeting) => void;
  onEdit: (meeting: Meeting) => void;
}

export function MeetingCard({ meeting, canEdit, onOpen, onEdit }: MeetingCardProps) {
  return (
    <div
      className="group flex cursor-pointer flex-col gap-3 rounded-card border border-outline bg-surface p-4 shadow-surface-1 transition-all hover:border-primary/30 hover:shadow-surface-2"
      onClick={() => onOpen(meeting)}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <h3 className="min-w-0 truncate text-body-md font-bold text-on-surface">{meeting.title}</h3>
        <div className="flex shrink-0 items-center gap-1.5">
          <Badge tone={STATUS_TONE[meeting.status] ?? 'neutral'}>{STATUS_LABEL[meeting.status]}</Badge>
          {canEdit && (
            <button
              type="button"
              aria-label="Edit meeting"
              className="text-on-surface-variant opacity-0 transition-opacity hover:text-primary group-hover:opacity-100"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(meeting);
              }}
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* When */}
      <div className="flex items-center gap-1.5 text-[12px] text-on-surface-variant">
        <CalendarClock className="h-3.5 w-3.5" />
        {formatWhen(meeting.meeting_date, meeting.meeting_time)}
      </div>

      {/* Join link */}
      {meeting.link && (
        <a
          href={meeting.link}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-1 text-[12px] font-semibold text-primary hover:underline"
        >
          <ExternalLink className="h-3.5 w-3.5" />
          Join Meeting
        </a>
      )}

      {/* Organizer */}
      {meeting.organizer_name && (
        <div className="mt-auto flex items-center gap-1.5 border-t border-outline pt-2 text-[11px] text-on-surface-variant">
          <User className="h-3 w-3" />
          <span className="truncate">Organized by {meeting.organizer_name}</span>
        </div>
      )}
    </div>
  );
}
